<template>
    <div class="iframe-container" v-bind:class="{ 'has-error': errorMsg }">
        <div v-if="errorMsg" class="error">
            {{ errorMsg }}
        </div>
        <div
            v-else
            v-bind:id="CONTAINER_ID"
            class="betGeniusStream"
        />
    </div>
</template>

<script lang="ts" setup>
    import { useEventListener, useScriptTag } from '@vueuse/core';
    import { storeToRefs } from 'pinia';
    import { computed, onMounted, onUnmounted, ref } from 'vue';
    import { LiveStreamResource } from '@/interface/enum';
    import Api from '@/libraries/api';
    import { getBetGeniusScriptUrl } from '@/libraries/url';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';

    defineOptions({
        name: 'BetGeniusLiveStream',
    });

    const props = defineProps({
        matchId: {
            type: Number,
            required: true,
        },
        providerMatchId: {
            type: Number,
            required: true,
        },
    });

    const errorMsg = ref('');
    const isPlayerReady = ref(false);

    const BET_GENIUS_MESSAGE_BUS = 'geniussportsmessagebus';
    const CONTAINER_ID = 'bet-genius-player-container';

    async function onMessage(event: Event) {
        try {
            const customEvent = event as CustomEvent;
            if (customEvent.detail?.type === 'player_ready') {
                isPlayerReady.value = true;
                const { url, errorCode, errorMessage } = await Api.getLiveStream(props.matchId, LiveStreamResource.BetGenius);

                if (errorCode === 0 && url) {
                    const parsed = JSON.parse(url);
                    window.GeniusLivePlayer.player.start(parsed);
                } else if (errorMessage) {
                    errorMsg.value = errorMessage;
                }
            } else if (customEvent.detail?.type === 'player_not_ready') {
                const errorsArray = customEvent.detail.body.error;
                errorMsg.value = `Error code: ${errorsArray[0]?.code}. ${errorsArray[0]?.message}.`;
            }
        } catch (error) {
            errorMsg.value = `Failed to load live stream. ${error}`;
        }
    }

    const { betGeniusCustomerId } = storeToRefs(useFeatureToggleStore());
    const src = computed(() => getBetGeniusScriptUrl(
        betGeniusCustomerId.value,
        props.providerMatchId.toString(),
        CONTAINER_ID,
    ));

    useEventListener(window, BET_GENIUS_MESSAGE_BUS, onMessage);

    const { load, unload } = useScriptTag(
        src,
        () => { },
        { manual: true },
    );

    onMounted(async () => {
        await load();
    });

    onUnmounted(() => {
        if (isPlayerReady.value && window.GeniusLivePlayer?.player) {
            try {
                window.GeniusLivePlayer.player.close();
            } catch {
                // Third-party SDK can throw internally (e.g. closeGSPlayer is not a function)
            }
        }
        unload();
    });
</script>

<style lang="scss" scoped>
.iframe-container {
    position: relative;
    width: 100%;
    padding-top: 56.25%; /* 16:9 ratio */
    background-color: rgba(0, 0, 0, 10%);
}

.betGeniusStream {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

.error {
    position: absolute;
    top: 0;
    display: flex;
    width: 100%;
    height: 56.25vw;
    align-items: center;
    justify-content: center;
    padding: 3em;
    background-color: rgba(0, 0, 0, 80%);
    color: white;
}
</style>
